import { Nullable } from '@orcar/common';
import {
  convertToKST,
  filterInsuranceAtom,
  filterOptionAtom,
  getHMFromNumber,
  getPrimaryColor,
  getSureUserId,
  HomePopup,
  isMobileAtom,
  JEJURO,
  MODAL_TYPE,
  PAGE_NAME,
  setStaticDate,
  useGetVehicleModels,
  useRecoil,
} from '@orcar/common-d2c';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KakaoChannelButton } from '@/components/button/KakaoChannelButton';
import ReserveLoadingOverlay from '@/components/reservation/ReserveLoadingOverlay';
import { useHomeHero, useHomePopup } from '@/hooks/useHomeImages';
import { useReserveAndPay } from '@/hooks/useReserveAndPay';
import { useContactProfile } from '@/hooks/useSiteProfile';
import {
  trackBannerClicked,
  trackBannerViewed,
  trackCarDetailedViewed,
  trackHomepageViewed,
  trackSearchStarted,
} from '@/utils/analytics';
import HeroSearchBar from './components/hero/HeroSearchBar';
import HeroSlider from './components/hero/HeroSlider';
import HomeInfoSections from './components/info/HomeInfoSections';
import HomeLocationSection from './components/location/HomeLocationSection';
import MobileSearchBox from './components/MobileSearchBox';
import PremiumCarSection, {
  SelectCarParams,
} from './components/premiumCar/PremiumCarSection';
import * as S from './styles';
import { MobileOptionSearchModal } from '../../components/modal/MobileOptionSearchModal';

const HomeLayout = () => {
  const navigate = useNavigate();
  const { items: heroItems } = useHomeHero();
  const { items: popupItems } = useHomePopup();
  const { kakaoChannelUrl } = useContactProfile();
  const sureUserId = getSureUserId();

  const [isModalOpen, setIsModalOpen] = useState<Nullable<string>>(null);

  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const { resetRecoilState: resetFilterOption } = useRecoil(filterOptionAtom);
  const { resetRecoilState: resetFilterInsurance } =
    useRecoil(filterInsuranceAtom);

  const { reserveAndPay, isReserving } = useReserveAndPay();

  const [pickUpDate, setPickUpDate] = useState<Dayjs>(
    dayjs(setStaticDate('pickUp')),
  );
  const [dropOffDate, setDropOffDate] = useState<Dayjs>(
    dayjs(setStaticDate('dropOff')),
  );
  const [pickUpTimeValue, setPickUpTimeValue] = useState(800);
  const [dropOffTimeValue, setDropOffTimeValue] = useState(800);

  const pickTimeValue = getHMFromNumber(pickUpTimeValue);
  const dropTimeValue = getHMFromNumber(dropOffTimeValue);

  const pickUpAt = (pickUpDate || dayjs())
    .set('hour', pickTimeValue.hh)
    .set('minute', pickTimeValue.mm);
  const dropOffAt = (dropOffDate || dayjs())
    .set('hour', dropTimeValue.hh)
    .set('minute', dropTimeValue.mm);

  // 목록 조회와 카드 클릭 예약을 동일한 날짜/시간으로 맞춰야
  // "그 날짜에 재고 없음"(NO_AVAILABLE_VEHICLE) 오류가 나지 않는다.
  const { data: vehicleModelList } = useGetVehicleModels({
    params: {
      pickUpAt: convertToKST(pickUpAt),
      dropOffAt: convertToKST(dropOffAt),
      driverAge: undefined,
      numberOfPeople: undefined,
      drivingExperience: undefined,
      sureUserId: sureUserId ? Number(sureUserId) : undefined,
    },
    // 날짜 선택 도중에는 반납일이 대여일보다 늦지 않은 상태가 되므로 조회하지 않는다.
    options: { enabled: dropOffAt.isAfter(pickUpAt, 'minute') },
  });

  const handleSelectCar = ({
    vehicleModelId,
    insuranceName,
    carType,
    carModel,
  }: SelectCarParams) => {
    if (isReserving) return;
    // 홈 인기차량 클릭도 검색결과와 동일하게 차량 선택 이벤트를 남긴다(퍼널 일관성).
    trackCarDetailedViewed({
      car_id: String(vehicleModelId),
      car_model: carModel,
      car_type: carType,
      is_with_pet: false,
      source: 'home_popular',
    });
    // 선택한 차량으로 사전예약 생성 후 결제 페이지로 이동한다.
    reserveAndPay({
      vehicleModelId,
      carType,
      insuranceName,
      pickUpAt: pickUpAt.format(),
      dropOffAt: dropOffAt.format(),
      isWithPet: false,
      source: 'home_popular',
    });
  };

  const handleBannerView = ({ id, index }: { id: string; index: number }) => {
    trackBannerViewed({ banner_id: id, banner_index: index, page: 'home' });
  };

  const handleBannerClick = ({ id, index }: { id: string; index: number }) => {
    trackBannerClicked({ banner_id: id, banner_index: index, page: 'home' });
    // 히어로 배너는 예약 시작 CTA로 동작한다(검색 실행).
    handleGetProducts();
  };

  const handleGetProducts = () => {
    if (!!pickUpAt && !!dropOffAt) {
      // 검색 시작 이벤트 트래킹
      trackSearchStarted({
        pickUpDate: pickUpAt.format('YYYY-MM-DD'),
        dropOffDate: dropOffAt.format('YYYY-MM-DD'),
      });

      navigate({
        pathname: PAGE_NAME.search,
        search: new URLSearchParams({
          pickUpAt: convertToKST(pickUpAt),
          dropOffAt: convertToKST(dropOffAt),
        }).toString(),
      });
      if (isMobile) {
        setIsModalOpen(null);
      }
    }
  };

  useEffect(() => {
    if (location.pathname === '/') {
      resetFilterOption();
      resetFilterInsurance();
      // 홈페이지 방문 이벤트 트래킹
      trackHomepageViewed();
    }
  }, [location.pathname]);

  return (
    <S.Wrapper isMobile={isMobile}>
      <HomePopup
        items={popupItems}
        dismissKey='jejuro_promo_modal_dismissed_until'
        accentColor={getPrimaryColor(JEJURO)}
        autoplayMs={1000}
      />
      {isReserving && <ReserveLoadingOverlay />}
      <HeroSlider
        isMobile={isMobile}
        items={heroItems}
        onBannerView={handleBannerView}
        onBannerClick={handleBannerClick}
        searchSlot={
          isMobile ? (
            <MobileSearchBox
              pickUpAt={pickUpAt}
              dropOffAt={dropOffAt}
              onRentalPeriodClick={() =>
                setIsModalOpen(MODAL_TYPE.MOBILE_SEARCH)
              }
              onSearchButtonClick={handleGetProducts}
            />
          ) : (
            <HeroSearchBar
              pickUpDate={pickUpDate}
              dropOffDate={dropOffDate}
              setPickUpDate={setPickUpDate}
              setDropOffDate={setDropOffDate}
              pickUpTimeValue={pickUpTimeValue}
              dropOffTimeValue={dropOffTimeValue}
              setPickUpTimeValue={setPickUpTimeValue}
              setDropOffTimeValue={setDropOffTimeValue}
              pickUpAt={pickUpAt}
              dropOffAt={dropOffAt}
              onSearch={handleGetProducts}
            />
          )
        }
      />

      <PremiumCarSection
        vehicleModelList={vehicleModelList}
        onSelect={handleSelectCar}
        isMobile={isMobile}
      />

      <HomeInfoSections isMobile={isMobile} />

      <HomeLocationSection isMobile={isMobile} />

      <MobileOptionSearchModal
        showModal={isModalOpen === MODAL_TYPE.MOBILE_SEARCH}
        onClose={() => setIsModalOpen(null)}
        pickUpDate={pickUpDate}
        setPickUpDate={setPickUpDate}
        dropOffDate={dropOffDate}
        setDropOffDate={setDropOffDate}
        pickUpTimeValue={pickUpTimeValue}
        setPickUpTimeValue={setPickUpTimeValue}
        dropOffTimeValue={dropOffTimeValue}
        setDropOffTimeValue={setDropOffTimeValue}
        handleNextBtn={handleGetProducts}
      />
      {kakaoChannelUrl && <KakaoChannelButton linkUrl={kakaoChannelUrl} />}
    </S.Wrapper>
  );
};

export default HomeLayout;
